import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { LeadMessages } from './components/LeadMessages';
import { LeadInformation } from './components/LeadInformation';
import { LeadNotes } from './components/LeadNotes';
import LeadActivity from './components/LeadActivity';
import { Lead, LeadStages } from 'models/Lead';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import LeadsContext from '../../providers/Leads/Leads.context';
import {
  DrawerNavigation,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { LeadDrawerOptions, LEAD_DRAWER_OPTIONS } from '../../../../models';
import ListingContext from '../../providers/Listing/Listing.context';
import IdCardIcon from 'icons/IdCard.icon';
import ChatIcon from 'icons/Chat.icon';
import ClockIcon from 'icons/Clock.icon';
import DocumentWithPencilIcon from 'icons/DocumentWithPencil.icon';
import useQueryParams from 'hooks/useQueryParams';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import DocumentsIcon from 'icons/Documents.icon';
import LeadFiles from './components/LeadFiles';
import {
  getArrayLength,
  getIsDesc,
  getSortValue,
  getUserFullName,
} from 'utils';
import ReleaseOrderPausedBanner from './components/ReleaseOrderPausedBanner';
import {
  getCanReplyInChat,
  getPausedLeadMixpanelProps,
  PAUSED_LEAD_REPLY_TABS,
} from './components/ReleaseOrderPausedBanner/utils';
import { getIsLeadReleaseOrderWaitingOnBroker } from '../../utils';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import {
  PAUSED_LEAD_LOCATION,
  PausedLeadReplyOpenedEventProps,
} from 'models/MixpanelEvents';
import DealContext from '../../providers/Deal/Deal.context';

import './ManageLeadDrawer.styles.scss';

const drawerNavigationTabs: Record<
  LeadDrawerOptions,
  { icon: ReactNode; value: LeadDrawerOptions; condition?: keyof Lead }
> = {
  [LEAD_DRAWER_OPTIONS.PROFILE]: {
    icon: <IdCardIcon className="bigger without-stroke" />,
    value: 'profile',
  },
  [LEAD_DRAWER_OPTIONS.ACTIVITY]: {
    icon: <ClockIcon className="stroke" />,
    value: 'activity',
  },
  [LEAD_DRAWER_OPTIONS.FILES]: {
    icon: <DocumentsIcon className="padding stroke" />,
    value: 'files',
  },
  [LEAD_DRAWER_OPTIONS.MESSAGES]: {
    icon: <ChatIcon className="stroke" />,
    value: 'chat',
    condition: 'sessionId',
  },
  [LEAD_DRAWER_OPTIONS.NOTES]: {
    icon: <DocumentWithPencilIcon className="bigger" />,
    value: 'notes',
  },
};

type ManageLeadDrawerProps = {
  className?: string;
} & DrawerNavigation;

const ManageLeadDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageLeadDrawerProps
> = ({ className, onPrev, onNext }, ref) => {
  const classes = classNames('manage-lead-drawer', className);

  const { t } = useTranslation();
  const {
    selectedLead,
    selectedLeadDrawer,
    setSelectedLeadDrawer,
    refetchLead,
    closeLeadDrawer,
    updateLead,
    openWithdrawModal,
  } = useContext(LeadsContext);
  const { areActionsDisabled, dealRoom } = useContext(ListingContext);
  const { leadStagesCopies } = useContext(DealContext);

  const getMixpanelProps = useGetMixpanelProps();

  const {
    params: { $sort, leadId, sessionId },
    setQueryParam,
    removeQueryParam,
  } = useQueryParams();

  const { _id: selectedLeadId, firstName, lastName } = selectedLead || {};

  const [hasNotes, setHasNotes] = useState(false);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const selectedDrawer = selectedLeadDrawer || LEAD_DRAWER_OPTIONS.PROFILE;

  const isMobile = useMatchMedia('isMobile');

  const showSortButtonNotes = useMemo(
    () => !isMobile && selectedDrawer === LEAD_DRAWER_OPTIONS.NOTES && hasNotes,
    [isMobile, selectedDrawer, hasNotes],
  );

  const drawerContent = useMemo(
    () => ({
      [LEAD_DRAWER_OPTIONS.PROFILE]: <LeadInformation />,
      [LEAD_DRAWER_OPTIONS.ACTIVITY]: <LeadActivity />,
      [LEAD_DRAWER_OPTIONS.FILES]: <LeadFiles />,
      [LEAD_DRAWER_OPTIONS.MESSAGES]: <LeadMessages />,
      [LEAD_DRAWER_OPTIONS.NOTES]: <LeadNotes onNotesChange={setHasNotes} />,
    }),
    [],
  );

  useImperativeHandle(ref, () => drawer, [drawer]);

  const isDesc = getIsDesc($sort);

  const onClose = useCallback(() => {
    if (selectedLeadId) refetchLead(selectedLeadId);

    closeLeadDrawer();
    setHasNotes(false);

    if (leadId) removeQueryParam('leadId');
    if (sessionId) removeQueryParam('sessionId');
  }, [
    closeLeadDrawer,
    leadId,
    refetchLead,
    removeQueryParam,
    selectedLeadId,
    sessionId,
  ]);

  const navItems = useMemo(
    () =>
      Object.values(drawerNavigationTabs).filter(
        ({ condition }) => !condition || !!selectedLead?.[condition],
      ),
    [selectedLead],
  );

  const pausedLeadMixpanelProps = useMemo(
    () => ({
      ...getPausedLeadMixpanelProps(selectedLead, dealRoom),
      stageName: leadStagesCopies?.[selectedLead?.leadState as LeadStages],
    }),
    [selectedLead, dealRoom, leadStagesCopies],
  );

  const onReplyInChat = useCallback(() => {
    mixpanelService.pausedLeadReplyOpened(
      getMixpanelProps({
        ...pausedLeadMixpanelProps,
        fromTab: PAUSED_LEAD_REPLY_TABS[selectedDrawer],
      }) as PausedLeadReplyOpenedEventProps,
    );

    setSelectedLeadDrawer(LEAD_DRAWER_OPTIONS.MESSAGES);
  }, [
    pausedLeadMixpanelProps,
    selectedDrawer,
    getMixpanelProps,
    setSelectedLeadDrawer,
  ]);

  const sortNotes = useCallback(
    () => setQueryParam('$sort', getSortValue('createdAt', $sort && !isDesc)),
    [$sort, isDesc, setQueryParam],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      {...(selectedLead && { title: getUserFullName(firstName, lastName) })}
      closeAction={onClose}
      onPrev={onPrev}
      onNext={onNext}
    >
      <header>
        <div className="manage-lead-drawer__navbar">
          {getArrayLength(navItems) > 1 &&
            navItems.map(({ icon, value, condition }) => {
              const isDisabled = condition && areActionsDisabled;
              return (
                <Button
                  key={value}
                  icon={icon}
                  disabled={isDisabled}
                  className={classNames({ selected: value === selectedDrawer })}
                  {...(!isDisabled && {
                    onClick: () => setSelectedLeadDrawer(value),
                  })}
                >
                  {t(value)}
                </Button>
              );
            })}
        </div>

        {showSortButtonNotes && (
          <Button
            variant="link"
            onClick={sortNotes}
            iconPosition="right"
            icon={ArrowdownIcon}
            className={classNames({
              rotated: $sort && !isDesc,
            })}
          >
            {t('sortNotes')}
          </Button>
        )}
      </header>

      <ReleaseOrderPausedBanner
        leadId={selectedLeadId}
        isEnabled={getIsLeadReleaseOrderWaitingOnBroker(selectedLead)}
        onResumed={() =>
          updateLead({
            ...selectedLead,
            isReleaseOrderPaused: false,
            isReleaseOrderAwaitingReaction: false,
          })
        }
        onWithdraw={() => openWithdrawModal(selectedLead)}
        location={PAUSED_LEAD_LOCATION.LEAD_DRAWER}
        mixpanelProps={pausedLeadMixpanelProps}
        {...(getCanReplyInChat(selectedLead, selectedDrawer) && {
          onReply: onReplyInChat,
        })}
      />

      {drawerContent[selectedDrawer]}
    </SideDrawer>
  );
};

export default forwardRef(ManageLeadDrawer);
